import { useCallback, useEffect, useRef } from "react";
import {
  useTripperScrollContext,
  useTripperWidgetContext,
} from "../context/TripperWidgetContext";
import { NavigableItem } from "../utils/flattenNavigableItems";

type UseCarouselSyncOptions = {
  containerRef: React.RefObject<HTMLDivElement | null>;
  items: NavigableItem[];
  enabled: boolean;
};

/**
 * Detects which carousel card is centered and updates the scroll context.
 * Also exposes a `scrollToItem` function for programmatic navigation
 * (e.g., when a map marker is tapped).
 */
export function useCarouselSync({
  containerRef,
  items,
  enabled,
}: UseCarouselSyncOptions) {
  const {
    setActiveItemId,
    setActiveSegmentId,
    setActiveStopGroupId,
  } = useTripperScrollContext();
  const { setIsAutoFollowActive } = useTripperWidgetContext();

  // Guard against sync loops: skip context updates during programmatic scrolls
  const isProgrammaticScroll = useRef(false);
  const programmaticTimer = useRef<ReturnType<typeof setTimeout>>();

  const findCenteredIndex = useCallback(() => {
    const container = containerRef.current;
    if (!container) return null;

    const containerRect = container.getBoundingClientRect();
    const centerX = containerRect.left + containerRect.width / 2;

    let closestIndex = -1;
    let closestDistance = Infinity;

    const cards = container.querySelectorAll<HTMLElement>("[data-carousel-index]");
    for (const card of cards) {
      const rect = card.getBoundingClientRect();
      const cardCenter = rect.left + rect.width / 2;
      const distance = Math.abs(cardCenter - centerX);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = parseInt(card.dataset.carouselIndex ?? "-1", 10);
      }
    }

    return closestIndex >= 0 ? closestIndex : null;
  }, [containerRef]);

  /** Update context to reflect a given item index */
  const activateItem = useCallback(
    (index: number) => {
      const item = items[index];
      if (!item) return;

      if (item.type === "stop") {
        setActiveItemId(item.stop._id);
        setActiveSegmentId(item.segmentId);
        setActiveStopGroupId(item.groupId ?? null);
      }
    },
    [items, setActiveItemId, setActiveSegmentId, setActiveStopGroupId],
  );

  // Update scale on all cards based on distance from center.
  // Scale is applied to the inner `.tripper-carousel__card-column` element,
  // NOT the snap wrapper, so scroll-snap isn't disrupted.
  const updateCardScales = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    const centerX = containerRect.left + containerRect.width / 2;

    const cards = container.querySelectorAll<HTMLElement>("[data-carousel-index]");
    for (const card of cards) {
      const rect = card.getBoundingClientRect();
      const cardCenter = rect.left + rect.width / 2;
      const distance = Math.abs(cardCenter - centerX);
      const scale = Math.max(0.9, 1 - (distance / containerRect.width) * 0.2);
      // Scale the inner content, not the snap-aligned wrapper
      const inner = card.firstElementChild as HTMLElement | null;
      if (inner) {
        inner.style.transform = `scale(${scale})`;
      }
    }
  }, [containerRef]);

  // Scroll handler: detect centered card, update context
  useEffect(() => {
    if (!enabled) return;
    const container = containerRef.current;
    if (!container) return;

    let rafId: number;
    const onScroll = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        updateCardScales();

        // Skip context updates during programmatic scrolls
        if (isProgrammaticScroll.current) return;

        const index = findCenteredIndex();
        if (index === null) return;

        // User-initiated swipe → re-enable auto-follow
        setIsAutoFollowActive(true);
        activateItem(index);
      });
    };

    container.addEventListener("scroll", onScroll, { passive: true });
    // Initial scale update
    requestAnimationFrame(updateCardScales);

    return () => {
      container.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(rafId);
    };
  }, [enabled, containerRef, findCenteredIndex, updateCardScales, activateItem, setIsAutoFollowActive]);

  /** Programmatically scroll the carousel to an item by its _id */
  const scrollToItem = useCallback(
    (itemId: string) => {
      const container = containerRef.current;
      if (!container) return;

      const target = container.querySelector<HTMLElement>(`[data-item-id="${itemId}"]`);
      if (!target) return;

      const containerRect = container.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      const scrollLeft =
        container.scrollLeft +
        (targetRect.left + targetRect.width / 2) -
        (containerRect.left + containerRect.width / 2);

      // Mark as programmatic to prevent the scroll handler from also updating
      isProgrammaticScroll.current = true;
      clearTimeout(programmaticTimer.current);

      // Update context immediately so the active state changes right away
      const index = items.findIndex(item => {
        if (item.type === "stop") return item.stop._id === itemId;
        return false;
      });
      if (index >= 0) {
        setIsAutoFollowActive(true);
        activateItem(index);
      }

      container.scrollTo({ left: scrollLeft, behavior: "smooth" });

      // Reset programmatic flag after scroll settles
      programmaticTimer.current = setTimeout(() => {
        isProgrammaticScroll.current = false;
        updateCardScales();
      }, 500);
    },
    [containerRef, items, activateItem, updateCardScales, setIsAutoFollowActive],
  );

  return { scrollToItem };
}
